import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const servicesDropdown = [
  'Auto Body Repair',
  'Auto Paint & Color Matching',
  'Frame & Structural Repair',
  'Dent & Bumper Repair',
  'Auto Glass',
  'Auto Detailing',
  'Rapid Repair Services',
  'Insurance Claim Assistance',
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id: string) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 w-full">
      {/* Top utility bar */}
      <div className="bg-black border-b border-yellow-700/30 w-full">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-11 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a
              href="https://www.google.com/maps/place/The+Royal+Collision+Centre/@33.1386319,-117.0509717,17z"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex items-center gap-2 text-yellow-500/80 hover:text-yellow-400 transition-colors duration-300 text-xs tracking-widest uppercase cursor-pointer"
            >
              <i className="ri-map-pin-line text-sm"></i>
              <span className="hidden sm:inline">2127 E Valley Pkwy, Escondido, CA</span>
            </a>
            <span className="flex items-center gap-2 text-yellow-500/60 text-xs tracking-widest uppercase">
              <i className="ri-time-line text-sm"></i>
              <span className="hidden sm:inline">Mon–Fri</span> 7AM–5PM
            </span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="tel:7606664151"
              className="whitespace-nowrap flex items-center gap-2 px-4 py-1.5 text-xs tracking-widest uppercase text-yellow-400 bg-gradient-to-r from-purple-900/80 to-black border border-purple-700/50 rounded-full hover:border-yellow-600/60 transition-all duration-300 cursor-pointer"
            >
              <i className="ri-phone-line"></i>
              <span className="hidden sm:inline">Call Now</span>
            </a>
            <button
              onClick={() => scrollTo('contact')}
              className="whitespace-nowrap flex items-center gap-2 px-4 py-1.5 text-xs tracking-widest uppercase text-yellow-300 bg-gradient-to-r from-purple-800 to-purple-950 border border-yellow-600/40 rounded-full hover:border-yellow-500 hover:from-purple-700 transition-all duration-300 cursor-pointer"
            >
              <i className="ri-file-list-3-line"></i>
              Get an Estimate
            </button>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <nav className={`w-full transition-all duration-500 ${scrolled ? 'bg-black/95 backdrop-blur-md border-b border-yellow-700/20' : 'bg-black'}`}>
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-24 md:h-28 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <img
              src="https://static.readdy.ai/image/ec66d0a10826ab7a25aab03ee5f0d5e7/0301b63c807ec203f7bc405cc6d9f185.png"
              alt="The Royal Collision Centre Logo"
              className="h-[72px] md:h-28 w-auto object-contain cursor-pointer"
              onClick={() => scrollTo('home')}
            />
          </div>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-10">
            {[
              { label: 'Home', id: 'home' },
              { label: 'Services', id: 'services' },
              { label: 'About Us', id: 'about' },
              { label: 'Reviews', id: 'reviews' },
              { label: 'Contact', id: 'contact' },
            ].map((item) =>
              item.label === 'Services' ? (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}
                >
                  <button
                    onClick={() => scrollTo(item.id)}
                    className="whitespace-nowrap flex items-center gap-1 text-sm tracking-[0.18em] uppercase text-yellow-400 hover:text-yellow-300 transition-colors duration-300 cursor-pointer font-medium"
                  >
                    {item.label}
                    <i className="ri-arrow-down-s-line text-base"></i>
                  </button>
                  {servicesOpen && (
                    <div className="absolute top-full left-0 mt-2 w-64 bg-[#0d0d0d] border border-yellow-700/30 rounded-lg py-2 shadow-2xl">
                      {servicesDropdown.map((s) => (
                        <button
                          key={s}
                          onClick={() => { scrollTo('services'); setServicesOpen(false); }}
                          className="whitespace-nowrap w-full text-left px-5 py-2.5 text-xs tracking-widest uppercase text-yellow-500/70 hover:text-yellow-400 hover:bg-yellow-900/10 transition-all duration-200 cursor-pointer"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <button
                  key={item.label}
                  onClick={() => scrollTo(item.id)}
                  className="whitespace-nowrap text-sm tracking-[0.18em] uppercase text-yellow-400 hover:text-yellow-300 transition-colors duration-300 cursor-pointer font-medium"
                >
                  {item.label}
                </button>
              )
            )}
            <button
              onClick={() => scrollTo('contact')}
              className="whitespace-nowrap ml-4 px-7 py-3 text-sm tracking-widest uppercase text-yellow-300 bg-gradient-to-r from-purple-800 to-black border border-yellow-600/40 rounded-full hover:border-yellow-500 hover:from-purple-700 transition-all duration-300 cursor-pointer font-medium"
            >
              Get Estimate
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden w-10 h-10 flex items-center justify-center text-yellow-500 cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'}`}></i>
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-black border-t border-yellow-700/20 px-6 py-6 flex flex-col gap-5">
            {[
              { label: 'Home', id: 'home' },
              { label: 'Services', id: 'services' },
              { label: 'About Us', id: 'about' },
              { label: 'Reviews', id: 'reviews' },
              { label: 'Contact', id: 'contact' },
            ].map((item) => (
              <button
                key={item.label}
                onClick={() => scrollTo(item.id)}
                className="whitespace-nowrap text-left text-xs tracking-[0.2em] uppercase text-yellow-500/80 hover:text-yellow-400 transition-colors duration-300 cursor-pointer"
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={() => scrollTo('contact')}
              className="whitespace-nowrap mt-2 px-6 py-3 text-xs tracking-widest uppercase text-yellow-300 bg-gradient-to-r from-purple-800 to-black border border-yellow-600/40 rounded-full hover:border-yellow-500 transition-all duration-300 cursor-pointer text-center"
            >
              Get Estimate
            </button>
          </div>
        )}
      </nav>
    </header>
  );
}
